import { Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { Invoice } from '@/api/generated/models';
import type { StatusHistoryEntry } from '@/features/history/statusHistoryTypes';
import { useGetInvoice, useGetInvoiceHistory } from '@/api/generated/invoices/invoices';
import { InvoiceStatus } from '@/api/generated/models/invoiceStatus';
import { BackButton, ContentCard } from '@/components/common';
import { StatusHistoryTable } from '@/features/history/components/StatusHistoryTable';
import { statusConfig } from '@/features/invoices/invoiceDisplay';

export const InvoiceHistoryPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: invoiceData, isLoading: isLoadingInvoice } = useGetInvoice(id ?? '', {
    query: { enabled: !!id }
  });
  const invoice = invoiceData as Invoice | undefined;
  const { data: historyData, isLoading: isLoadingHistory } = useGetInvoiceHistory(id ?? '', {
    query: { enabled: !!id }
  });
  const entries = (historyData as StatusHistoryEntry[] | undefined) ?? [];

  const resolveStatus = (raw: string) => {
    const config = statusConfig[raw as InvoiceStatus];
    return config
      ? { label: t(config.labelKey), bg: config.bg, fg: config.fg }
      : { label: raw, bg: 'var(--color-bg-surface-2)', fg: 'var(--color-text-secondary)' };
  };

  if (isLoadingInvoice || isLoadingHistory || !invoice) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={`/invoices/${id}`} />
        <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
          {invoice.invoiceNumber ?? invoice.referenceId}
        </Text>
        <ContentCard>
          <div style={{ padding: 20 }}>
            <StatusHistoryTable entries={entries} resolveStatus={resolveStatus} />
          </div>
        </ContentCard>
      </Stack>
    </Container>
  );
};
